import React, { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router-dom"

import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import MapCanvas from "../../../containers/GlobalMap/map/MapCanvas"
import { filterByActiveLayers } from "../../../containers/GlobalMap/map/fit.ts"
import { MapProviderError } from "../../../containers/GlobalMap/MapStates"
import { LayerToggleButtons } from "../../../containers/GlobalMap/filters/LayerToggleButtons.tsx"
import { selectActiveLayers } from "../../../containers/GlobalMap/redux/global-map.slice"
import { isRecordEntity } from "../../../containers/GlobalMap/redux/global-map.types"
import type {
  MapCluster,
  MapFeature,
  MapFeaturesResponse,
  MapLayer
} from "../../../containers/GlobalMap/redux/global-map.types"
import { parseFeatureId } from "../../../containers/GlobalMap/helpers/feature-id"
import { RoutesPaths } from "../../../constants/routes.ts"
import type { WidgetProps } from "../types"
import { WidgetFrame } from "../WidgetFrame"
import { getBoundsCenter, getBoundsZoomLevel } from "./getBoundsZoomLevel.ts"

type TileFit = { latitude: number; longitude: number; zoom: number }

// `fitBounds` (inside `MapCanvas`) picks its zoom from the CONTAINER'S OWN
// pixel size — confirmed live via a direct side-by-side comparison: the same
// 11-record dataset showed a "200km" scale bar in this tile and "100km" on
// the full `/map` page, because the tile's container is narrower/shorter
// than the full page's. Rather than hand `MapCanvas` raw points and let it
// fit to THIS tile's own cramped size, this computes an explicit
// `{latitude, longitude, zoom}` against the full page's own typical
// dimensions instead (`MapTab.tsx`'s `h-[620px]`, width generous enough that
// longitude never becomes the binding axis for a normal metro-area spread)
// — the tile then shows the same zoom level the full page would, just
// through a smaller window onto it, which is what "focus the map like the
// Location section does" turned out to mean once reproduced with real data.
const FULL_PAGE_REFERENCE_SIZE = { width: 1200, height: 620 }
const FIT_PADDING_PX = 48

const boundsFit = (points: { latitude: number; longitude: number }[]): TileFit => ({
  ...getBoundsCenter(points),
  zoom: getBoundsZoomLevel(points, FULL_PAGE_REFERENCE_SIZE, FIT_PADDING_PX)
})

// Mirrors `MapTab.handleOpenFeature` exactly, but without that component's
// `selectedFeatureId` indirection — a marker click here already carries its
// own `entity_type` straight from `markers.ts`'s `onSelectFeature(feature.id,
// feature.entity_type)`, so there is no need to look anything up first.
const routeForFeature = (featureId: string, entityType: "project" | "lead"): string => {
  const { id } = parseFeatureId(featureId)
  return entityType === "lead"
    ? RoutesPaths.lead.view(id)
    : RoutesPaths.project.view(id).overview
}

export const MapWidget = ({
  widget,
  payload,
  state,
  onRefresh,
  onExpand,
  onDrill,
  editChrome
}: WidgetProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const activeLayers = useAppSelector(selectActiveLayers)
  const map = payload?.map
  const [fitTo, setFitTo] = useState<TileFit | { bounds: { latitude: number; longitude: number }[] } | null>(null)
  // Mirrors `MapTab`'s own `providerErrorCode`/`handleRetryProvider` — a
  // `map_provider_unavailable` failure (a missing/rejected Google Maps key,
  // confirmed live to be an environment issue, not a code defect: the full
  // `/map` page hits the identical failure with the same key) is otherwise
  // invisible here, since `MapCanvas` itself renders nothing at all once its
  // internal `providerFailed` state trips. Swapping the JSX between
  // `MapProviderError` and `MapCanvas` at the same position unmounts/remounts
  // `MapCanvas` on retry, giving `loadGoogleMaps()` a fresh attempt — the same
  // mechanism the full page's own retry already relies on.
  const [providerErrorCode, setProviderErrorCode] = useState<string | null>(null)

  // `MapCanvas` only ever reads `mode`/`features`/`clusters` off its `data`
  // prop (confirmed in `markers.ts`) — a `MapWidgetPayload` already carries
  // exactly those three, so this is a direct reshape, not a lookup. Filtered
  // by `activeLayers` the same way `MapTab.tsx` filters the full page's own
  // data — the two surfaces share `global-map.slice`'s layer-toggle state, so
  // the tile and the full map always agree on which entity types are shown.
  const canvasData: Pick<MapFeaturesResponse, "mode" | "features" | "clusters"> | null = useMemo(
    () =>
      map
        ? filterByActiveLayers(
            { mode: map.mode, features: map.features as MapFeature[], clusters: map.clusters as MapCluster[] },
            activeLayers
          )
        : null,
    [map, activeLayers]
  )

  const effectiveFit = useMemo(() => {
    if (fitTo) return fitTo
    if (!canvasData) return null
    if (canvasData.mode === "features" && canvasData.features.length) return boundsFit(canvasData.features)
    if (canvasData.clusters.length) return boundsFit(canvasData.clusters)
    return null
  }, [fitTo, canvasData])

  const handleSelectFeature = useCallback(
    (featureId: string, entityType: MapLayer) => {
      // Only the two record layers have a page to drill into — a
      // Subcontractor/Appointment marker opens nothing from this widget.
      if (!isRecordEntity(entityType)) return
      const target = routeForFeature(featureId, entityType)
      if (onDrill) onDrill(target)
      else navigate(target)
    },
    [onDrill, navigate]
  )

  // Mirrors `MapTab.handleClusterClick` — recenters and zooms to the
  // cluster's own centroid at its server-provided `expansion_zoom`, with no
  // refetch: this tile's payload is already the complete unbounded set, so
  // there is nothing narrower to fetch for a closer look.
  const handleClusterClick = useCallback((cluster: MapCluster) => {
    setFitTo({ latitude: cluster.latitude, longitude: cluster.longitude, zoom: cluster.expansion_zoom })
  }, [])

  const handleMapError = useCallback((code: string) => {
    setProviderErrorCode(code)
  }, [])

  const handleRetryProvider = useCallback(() => {
    setProviderErrorCode(null)
  }, [])

  const isEmpty = !map || map.total === 0

  return (
    <WidgetFrame
      editChrome={editChrome}
      title={widget.config?.title ?? widget.title}
      state={isEmpty ? "empty" : state}
      capabilities={widget.capabilities}
      widgetId={widget.id}
      payload={payload}
      onRefresh={onRefresh}
      onExpand={onExpand}
    >
      {!isEmpty && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {/* Same layer-filter pills the full `/map` page's toolbar offers,
              reading/dispatching the SAME `global-map.slice` state — toggling
              a layer here also affects the full map page's own filter the
              next time it's opened, by design (one shared filter, two
              surfaces). Above the map rather than floating over it, per
              explicit product direction — the icon+label pair on each pill
              already does what a separate legend would, so there is no
              second, redundant Lead/Project key below the map either. */}
          {!providerErrorCode && (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <LayerToggleButtons />
            </div>
          )}
          {/* A fixed pixel height, not `flex: 1`/percentage-height: the Google
              Maps SDK sizes itself to whatever its container measures at
              construction time (inside `MapCanvas`'s mount effect) and never
              recovers if that's 0 — confirmed live, the map rendered a real
              internal DOM tree (Google's own map region) but at
              `height: 0`, since this widget sits inside the dashboard's own
              nested flex/grid layout, which doesn't guarantee a settled
              nonzero height for a `height: 100%` chain on first paint the
              way the full standalone `/map` page's layout does. 390px is the
              original 260px +50%, per explicit product direction once the
              tile's width shrank to match the Portfolio Overview column. */}
          <div className="relative" style={{ height: 390 }}>
            {providerErrorCode ? (
              <MapProviderError onRetry={handleRetryProvider} />
            ) : (
              <MapCanvas
                data={canvasData}
                fitTo={effectiveFit}
                selectedId={null}
                reducedMotion={false}
                nearCenter={null}
                nearRadiusMi={null}
                onSelectFeature={handleSelectFeature}
                onClusterClick={handleClusterClick}
                onViewportChange={() => {}}
                onError={handleMapError}
                onLocateMe={() => {}}
                locateMeLabel={t("globalMap.locateMe.label")}
                fullscreenLabel={t("globalMap.mapControls.fullscreen")}
                changeMapStyleLabel={t("globalMap.mapControls.changeMapStyle")}
                mapTypeLabel={t("globalMap.mapControls.mapType")}
                satelliteTypeLabel={t("globalMap.mapControls.satelliteType")}
              />
            )}
          </div>
          <Link to={RoutesPaths.map} style={{ fontWeight: 600 }}>
            {t("portfolio.map.insightLink", "Insight in Location")}
          </Link>
        </div>
      )}
    </WidgetFrame>
  )
}
